// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { useToast } from 'hooks';
import SignaturePad from 'react-signature-canvas';
import {
  AttachmentProps,
  AttachmentSuccessProps,
  SignatureFieldPreviewProps,
  signatoryProps,
} from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/type/preview.type';
import useCustomForm from 'pages/Forms/context/useCustomForm';
import useAttachment from '../../components/UploadAttachments/hooks/useAttachment';

export const useSignaturePreviewHooks = (
  element: SignatureFieldPreviewProps['element'],
  handleDirty?: SignatureFieldPreviewProps['handleDirty'],
  tableId?: string,
  rowIndex?: number,
  colIndex?: number,
  updateAnswer?: SignatureFieldPreviewProps['updateAnswer'],
) => {
  const toast = useToast();
  const sigCanvas = useRef<SignaturePad | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [, { setAnswer }] = useCustomForm();
  const [errorMessage, setErrorMessage] = useState('');
  const {
    fetchAttachmentData,
    stripPrefixInAttachmentID,
    saveAttachment,
    attachmentLoading,
    getFormData,
    removeSurroundingQuotes,
  } = useAttachment();

  // Check if the element is multi-signature
  const isMultiSign = element?.type === 'multisignature';

  // Initialize signatories based on whether it's multi-signature or not
  const [signatories, setSignatories] = useState<
    signatoryProps[] | signatoryProps
  >(
    // eslint-disable-next-line no-nested-ternary
    isMultiSign
      ? Array.isArray(element?.answer)
        ? element?.answer
        : []
      : element?.answer
      ? element.answer
      : {},
  );

  // Get attachment ID
  // eslint-disable-next-line no-nested-ternary
  const newAttachmentId = isMultiSign
    ? element?.answer?.map(
        (item: { attachmentId: string | null; id: string | null }) =>
          stripPrefixInAttachmentID(item.attachmentId ?? item.id),
      )
    : element?.answer
    ? stripPrefixInAttachmentID(
        element.answer.attachmentId ?? element.answer.id,
      )
    : null;

  // Ref to track if data has been fetched
  const hasFetchedData = useRef(false);

  // Attachment success callback
  const onSuccess = (data: AttachmentSuccessProps[]) => {
    setSignatories((prevSignatories: signatoryProps[] | signatoryProps) => {
      if (isMultiSign && Array.isArray(prevSignatories)) {
        const lastIndex = prevSignatories.length - 1;
        const rest = prevSignatories.slice(0, lastIndex);
        return [
          ...rest,
          {
            id: removeSurroundingQuotes(String(data[0]?.id)),
            attachmentId: removeSurroundingQuotes(String(data[0]?.id)),
            file: data[0]?.file,
            created_at: data[0]?.updated_at,
            signatory_name: prevSignatories[lastIndex]?.signatory_name,
          },
        ];
      }
      if (!isMultiSign && !Array.isArray(prevSignatories)) {
        return {
          id: removeSurroundingQuotes(String(data[0]?.id)),
          attachmentId: removeSurroundingQuotes(String(data[0]?.id)),
          file: data[0]?.file,
          created_at: data[0]?.updated_at,
          signatory_name: prevSignatories.signatory_name,
        };
      }

      return prevSignatories;
    });

    toast.success('Signature saved successfully');
    setIsDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    clear();
  };

  // Add signatory
  const addSignatory = () => {
    if (isMultiSign) {
      if (
        (Array.isArray(signatories) && signatories.length === 0) ||
        (Array.isArray(signatories) &&
          signatories[signatories.length - 1]?.file &&
          signatories[signatories.length - 1]?.signatory_name &&
          signatories[signatories.length - 1]?.signatory_name !==
            'digital-signature.png')
      ) {
        // First update the signatories state
        setSignatories((prevSignatories) => [
          ...(Array.isArray(prevSignatories) ? prevSignatories : []),
          { id: '', file: '', signatory_name: '' },
        ]);

        setTimeout(() => {
          setIsDialogOpen(true);
        }, 0);
      } else {
        setErrorMessage(
          'Please add both signature and signatory name before adding another signatory.',
        );
      }
    } else if (
      (!Array.isArray(signatories) && !signatories?.file) ||
      (!Array.isArray(signatories) && !signatories?.signatory_name) ||
      (!Array.isArray(signatories) &&
        signatories?.signatory_name === 'digital-signature.png')
    ) {
      setSignatories({ id: '', file: '', signatory_name: '' });

      // Same approach here
      setTimeout(() => {
        setIsDialogOpen(true);
      }, 0);
    } else {
      setErrorMessage(
        'Please add both signature and signatory name before saving.',
      );
    }
  };

  const save = (signatureName?: string) => {
    if (sigCanvas.current && !sigCanvas.current?.isEmpty()) {
      sigCanvas.current.getTrimmedCanvas().toBlob((blob: Blob | null) => {
        if (blob) {
          if (isMultiSign) {
            // Special handling for first save or empty array
            if (!Array.isArray(signatories) || signatories.length === 0) {
              setSignatories([
                {
                  id: '',
                  file: blob,
                  signatory_name: signatureName || '',
                },
              ]);
            } else {
              // Regular update for existing array
              setSignatories((prevSignatories) => {
                if (Array.isArray(prevSignatories)) {
                  return prevSignatories.map((signatory, index) =>
                    index === prevSignatories.length - 1
                      ? {
                          ...signatory,
                          file: blob,
                          signatory_name:
                            signatureName || signatory.signatory_name || '',
                        }
                      : signatory,
                  );
                }
                // Handle unexpected case - convert to array with one element
                return [
                  {
                    id: '',
                    file: blob,
                    signatory_name: signatureName || '',
                  },
                ];
              });
            }
          } else {
            // Single signature handling
            setSignatories({
              id: '',
              file: blob,
              signatory_name: signatureName || '',
            });
          }

          // Save attachment after state update
          saveAttachment(getFormData(blob), null, null, onSuccess);
        }
      }, 'image/png');

      handleCloseDialog();
      setErrorMessage('');
    } else {
      toast.error(
        'Please add both signature and signatory name before saving.',
      );
    }

    if (handleDirty) {
      handleDirty();
    }
  };

  // Toggle signature pad
  const toggleSignaturePad = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  // remove signature
  const removeSignature = (signatoryId?: string) => {
    if (isMultiSign && Array.isArray(signatories)) {
      const updatedSignatories: signatoryProps[] = signatories.filter(
        (signatory) => signatory?.id !== signatoryId,
      );
      setSignatories(updatedSignatories);
      if (tableId) {
        handleUpdateAnswer(updatedSignatories);
      } else {
        setAnswer(element?.id, updatedSignatories);
      }
    } else {
      setSignatories({});
      if (tableId) {
        handleUpdateAnswer({});
      } else {
        setAnswer(element?.id, {});
      }
    }
    if (handleDirty) {
      handleDirty();
    }
  };

  // clear signature
  const clear = () => {
    sigCanvas.current?.clear();
  };

  useEffect(() => {
    if (
      element?.answer &&
      !hasFetchedData.current &&
      (Array.isArray(element.answer)
        ? element.answer.length > 0
        : Object.keys(element.answer).length > 0)
    ) {
      fetchAttachmentData({
        query: { attachments: newAttachmentId },
        skip: false,
        onSuccess: (data: Record<string, AttachmentProps>) => {
          if (isMultiSign) {
            const formattedAttachments: signatoryProps[] = Object.keys(
              data,
            ).map((itemId) => {
              const signatoryName = Array.isArray(signatories)
                ? signatories.find(
                    (itm: signatoryProps) => itm?.attachmentId === itemId,
                  )
                : undefined;
              return {
                id: removeSurroundingQuotes(String(itemId)),
                attachmentId: removeSurroundingQuotes(String(itemId)),
                created_at: signatoryName?.created_at,
                signatory_name:
                  signatoryName?.signatory_name || signatoryName?.name,
                ...data[itemId],
              };
            });
            setSignatories(formattedAttachments);
          } else {
            const itemId = Object.keys(data)[0];
            setSignatories({
              id: removeSurroundingQuotes(String(itemId)),
              attachmentId: removeSurroundingQuotes(String(itemId)),
              created_at: Array.isArray(signatories)
                ? undefined
                : signatories?.created_at,
              signatory_name: Array.isArray(signatories)
                ? undefined
                : signatories?.signatory_name,
              ...data[itemId],
            });
          }
        },
      });
      hasFetchedData.current = true;
    }
  }, [element?.answer]);

  useEffect(() => {
    if (!tableId) {
      setAnswer(element?.id, signatories);
    } else {
      updateAnswer(tableId, rowIndex, colIndex, signatories);
    }
  }, [signatories]);

  // FOR TABLE ELEMENTS

  const handleUpdateAnswer = (signature) => {
    updateAnswer(tableId, rowIndex, colIndex, signature);
  };

  return {
    sigCanvas,
    signatories,
    setIsDialogOpen,
    isDialogOpen,
    errorMessage,
    attachmentLoading,
    addSignatory,
    handleCloseDialog,
    removeSignature,
    save,
    toggleSignaturePad,
    clear,
  };
};
